// ============================================================
//  src/admin/pages/AdminRentSpacePage.js
//
//  Configure the platform's own WhatsApp token/WABA/monthly price
//  for "Rent Space", and manage every rental: pause, resume, or
//  permanently delete (calls Meta's DELETE /{PHONE_NUMBER_ID} --
//  irreversible, guarded by a strict confirmation).
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getRentSpaceConfig, saveRentSpaceConfig,
  listRentSpaceRentals, pauseRental, resumeRental, deregisterRental, verifyRentalStatus,
  previewLeakedFlows, cleanupLeakedFlows, syncPlatformIdsToBots, listBotFlows, removeBotFlows,
} from '../api/adminClient';
import { PageHeader } from '../components/UI';

const EMPTY_CONFIG = { token: '', wabaId: '', appSecret: '', monthlyPrice: '', flowEndpointUrl: '', metaAppId: '', maxPerUser: '' };

const STATUS_META = {
  pending_payment: { label: 'Pending Payment', color: '#9a7b00', bg: '#fff8e1' },
  active:          { label: 'Active',          color: '#00875a', bg: '#e7f9f0' },
  paused:          { label: 'Paused',           color: '#6b7280', bg: '#f3f4f6' },
  deregistered:    { label: 'Deregistered',     color: '#c0392b', bg: '#fdeaea' },
};

// NOTE: Meta's API does not support deleting a phone number — confirmed
// directly against their current docs ("You can't delete a business
// phone number via the API. To delete a business phone number, use
// WhatsApp Manager."). What IS available is deregistration, which stops
// the number sending/receiving through Cloud API but does NOT remove it
// from the WABA's number list, free up a slot in the 20-number cap, or
// delete its message history. This modal is worded to reflect that
// honestly rather than promising a "delete" this platform can't do.
function DeregisterConfirmModal({ rental, onClose, onDeregistered }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy]   = useState(false);
  const expectedPhone = `${rental.rnCountryCode || ''}${rental.rnPhoneNumber || ''}`;

  async function handleDeregister() {
    setBusy(true);
    try {
      const res = await deregisterRental(rental.rnId, typed.trim());
      if (res.data?.success) {
        toast.success(res.data.message ?? 'Number deregistered');
        onDeregistered();
        onClose();
      } else {
        toast.error(res.data?.message ?? 'Could not deregister this number');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setBusy(false);
  }

  return (
    <div className="bf-modal-overlay" onClick={onClose}>
      <div className="bf-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="bf-modal-icon bf-modal-icon-danger">⚠️</div>
        <div className="bf-modal-title">Deregister Phone Number</div>
        <div className="bf-modal-message">
          This stops {expectedPhone} from sending or receiving messages through Cloud API immediately — any bot
          using this Phone Number ID stops working the instant this completes.
          <br /><br />
          <strong>This is not the same as deleting the number.</strong> Meta's API doesn't support deletion — it
          stays on the WABA's number list and still counts toward the 20-number cap. To fully delete it (and free up
          that slot), a business portfolio admin has to do that manually inside WhatsApp Manager, and only if the
          number hasn't sent a paid message in the last 30 days.
        </div>
        <div className="bf-field" style={{ marginTop: 16 }}>
          <label className="bf-field-label">
            Type the exact phone number (<code>{expectedPhone}</code>) to confirm
          </label>
          <input className="bf-input" value={typed} onChange={e => setTyped(e.target.value)} placeholder={expectedPhone} />
        </div>
        <div className="bf-modal-actions">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-danger" onClick={handleDeregister} disabled={busy || typed.trim() !== expectedPhone}>
            {busy ? 'Deregistering…' : 'Deregister Number'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Manual per-bot flow review/cleanup. Unlike LeakedFlowsPanel's automatic
// duplicate detection, this works even when a leaked flow only ever got
// synced onto ONE rental bot -- the common case, since it just takes one
// customer clicking "Sync" once to pull in everything on the shared WABA.
// The admin (working with the customer, who knows which flows are
// actually theirs) checks off the ones that aren't and removes them --
// local rows only, nothing on Meta is touched.
function BotFlowsReviewModal({ bId, onClose }) {
  const [flows, setFlows]     = useState(null); // null = loading
  const [checked, setChecked] = useState({});   // fId -> bool
  const [removing, setRemoving] = useState(false);

  const load = useCallback(() => {
    setFlows(null);
    listBotFlows(bId).then(res => {
      if (res.data?.success) setFlows(res.data.flows || []);
      else { toast.error(res.data?.message ?? 'Could not load flows'); setFlows([]); }
    }).catch(() => { toast.error('Network error'); setFlows([]); });
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  function toggle(fId) { setChecked(c => ({ ...c, [fId]: !c[fId] })); }
  const selectedIds = Object.keys(checked).filter(k => checked[k]); // fId is a string ID, not numeric -- keep as-is

  async function handleRemove() {
    if (!selectedIds.length) return;
    setRemoving(true);
    try {
      const res = await removeBotFlows(bId, selectedIds);
      if (res.data?.success) {
        toast.success(res.data.message ?? 'Removed');
        setChecked({});
        load();
      } else {
        toast.error(res.data?.message ?? 'Could not remove');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setRemoving(false);
  }

  return (
    <div className="bf-modal-overlay" onClick={onClose}>
      <div className="bf-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="bf-modal-title">Review Flows on This Bot</div>
        <div className="bf-modal-message">
          Check off any flow this customer didn't actually create — e.g. ones that leaked in from another renter
          before syncing was blocked for rented numbers — then remove them. This only removes the local row; nothing
          on Meta is touched, and any other renter's own copy is unaffected.
        </div>

        {flows === null ? (
          <div style={{ padding: '16px 0', fontSize: 13, color: '#9ca3af' }}>Loading…</div>
        ) : flows.length === 0 ? (
          <div style={{ padding: '16px 0', fontSize: 13, color: '#9ca3af' }}>This bot has no flows.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', margin: '14px 0' }}>
            {flows.map(f => (
              <label key={f.fId} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, cursor: 'pointer',
              }}>
                <input type="checkbox" checked={!!checked[f.fId]} onChange={() => toggle(f.fId)} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#111827' }}>{f.fName}</div>
                  <div style={{ fontSize: 11.5, color: '#9ca3af' }}>
                    {f.fCategory} · {f.fStatus} · {f.fCreatedAt ? new Date(f.fCreatedAt).toLocaleDateString() : ''}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}

        <div className="bf-modal-actions">
          <button className="btn btn-outline" onClick={onClose} disabled={removing}>Close</button>
          <button className="btn btn-danger" onClick={handleRemove} disabled={removing || !selectedIds.length}>
            {removing ? 'Removing…' : `Remove ${selectedIds.length || ''} Selected`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}

// Pushes the current platform token/WABA ID/App Secret/Meta App ID onto
// every existing rental bot in one pass -- covers rental bots created
// before Bot::createBot() was fixed to persist those fields at all (see
// that method's comment), and bots created before Meta App ID existed as
// a platform setting.
function SyncPlatformIdsPanel() {
  const [syncing, setSyncing] = useState(false);

  async function run() {
    setSyncing(true);
    try {
      const res = await syncPlatformIdsToBots();
      if (res.data?.success) toast.success(res.data.message ?? 'Synced');
      else toast.error(res.data?.message ?? 'Sync failed');
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setSyncing(false);
  }

  return (
    <div className="bf-card">
      <div className="bf-card-hdr">Sync to Existing Rented Bots</div>
      <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          Pushes the token, WABA ID, App Secret, and Meta App ID above onto every bot already linked to a rental —
          and repairs a blank Phone Number ID on any of them from that rental's own record (needed for encryption
          key setup, among other things).
          Useful right after changing any of those, or to fix an older rented bot that's missing one of them.
        </div>
        <button className="btn btn-outline" style={{ alignSelf: 'flex-start', fontSize: 12, padding: '6px 12px' }}
          onClick={run} disabled={syncing}>
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      </div>
    </div>
  );
}

// One-time repair panel for the Flow-sync leak: before "Sync from Meta"
// was blocked for rented numbers, any renter who clicked it pulled in
// every Flow on the shared platform WABA, including ones OTHER renters
// had created, as local rows on their own bot. This previews (and can
// remove) exactly those cross-contaminated rows -- see
// RentSpaceController::adminCleanupLeakedFlows for the detection logic.
// Nothing on Meta's side is touched, only the local duplicate rows.
function LeakedFlowsPanel() {
  const [preview, setPreview] = useState(null); // { groups, removeCount } | null
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  async function runPreview() {
    setLoading(true);
    try {
      const res = await previewLeakedFlows();
      if (res.data?.success) setPreview(res.data);
      else toast.error(res.data?.message ?? 'Could not check for leaked flows');
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setLoading(false);
  }

  async function runCleanup() {
    setCleaning(true);
    try {
      const res = await cleanupLeakedFlows();
      if (res.data?.success) {
        toast.success(res.data.message ?? 'Cleaned up');
        setPreview(null);
      } else {
        toast.error(res.data?.message ?? 'Cleanup failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setCleaning(false);
  }

  return (
    <div className="bf-card">
      <div className="bf-card-hdr">Leaked Flows Check (Rented Numbers)</div>
      <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          Before "Sync from Meta" was blocked for rented numbers, a renter who used it would pull in every Flow on
          the shared platform WABA — including ones other renters created — as local rows on their own bot. This
          checks for exactly that (and only that) and can clean it up. Nothing on Meta is touched, only local
          duplicate rows.
        </div>
        <button className="btn btn-outline" style={{ alignSelf: 'flex-start', fontSize: 12, padding: '6px 12px' }}
          onClick={runPreview} disabled={loading}>
          {loading ? 'Checking…' : 'Check for leaked flows'}
        </button>

        {preview && preview.removeCount === 0 && (
          <div style={{ fontSize: 13, color: 'var(--bf-success)' }}>✅ No cross-contaminated flows found.</div>
        )}

        {preview && preview.removeCount > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--bf-warning)' }}>
              Found {preview.removeCount} leaked flow row{preview.removeCount !== 1 ? 's' : ''} across {preview.groups.length} flow{preview.groups.length !== 1 ? 's' : ''}.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
              {preview.groups.map(g => (
                <div key={g.metaFlowId} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 12.5 }}>
                  <div style={{ marginBottom: 4 }}>
                    <strong>{g.keep.fName}</strong>{' '}
                    <span style={{ color: '#9ca3af' }}>— kept for {g.keep.ownerEmail || g.keep.ownerName}</span>
                  </div>
                  {g.remove.map(r => (
                    <div key={r.fId} style={{ color: '#c0392b' }}>
                      🗑 remove copy from {r.ownerEmail || r.ownerName} ("{r.fName}")
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <button className="btn btn-danger" style={{ alignSelf: 'flex-start', fontSize: 12, padding: '6px 14px' }}
              onClick={runCleanup} disabled={cleaning}>
              {cleaning ? 'Removing…' : `Remove ${preview.removeCount} leaked row(s)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminRentSpacePage() {
  const [form, setForm]           = useState(EMPTY_CONFIG);
  const [configured, setConfigured] = useState(false);
  const [numbersInUse, setNumbersInUse] = useState(0);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  const [rentals, setRentals]     = useState([]);
  const [rentalsLoading, setRentalsLoading] = useState(true);
  const [deregisterTarget, setDeregisterTarget] = useState(null); // rental row or null
  const [busyRnId, setBusyRnId]   = useState(null);
  const [reviewBId, setReviewBId] = useState(null); // bId currently open in the flow-review modal, or null

  const loadConfig = useCallback(() => {
    setLoading(true);
    getRentSpaceConfig().then(res => {
      const c = res.data.config;
      setForm({
        token: c.token || '',
        wabaId: c.wabaId || '',
        appSecret: c.appSecret || '',
        monthlyPrice: c.monthlyPrice || '',
        flowEndpointUrl: c.flowEndpointUrl || '',
        metaAppId: c.metaAppId || '',
        // '' displays as a blank input meaning "unlimited" -- 0 would
        // render as a literal 0 in a number input, which reads as "no
        // one can rent anything" rather than "no cap".
        maxPerUser: c.maxPerUser ? String(c.maxPerUser) : '',
      });
      setConfigured(c.configured);
      setNumbersInUse(c.numbersInUse || 0);
    }).finally(() => setLoading(false));
  }, []);

  const loadRentals = useCallback(() => {
    setRentalsLoading(true);
    listRentSpaceRentals().then(res => {
      if (res.data?.success) setRentals(res.data.rentals || []);
    }).finally(() => setRentalsLoading(false));
  }, []);

  useEffect(() => { loadConfig(); loadRentals(); }, [loadConfig, loadRentals]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await saveRentSpaceConfig(form);
      toast.success('Rent Space settings saved');
      loadConfig();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handlePauseResume(rental) {
    setBusyRnId(rental.rnId);
    try {
      const action = rental.rnStatus === 'active' ? pauseRental : resumeRental;
      const res = await action(rental.rnId);
      if (res.data?.success) {
        toast.success(res.data.message);
        loadRentals();
      } else {
        toast.error(res.data?.message ?? 'Action failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setBusyRnId(null);
  }

  // Checks Meta's live state for this number -- our own database only
  // knows what we told it, so if someone deleted this manually in
  // WhatsApp Manager, our list would keep showing it forever without
  // this actually asking Meta whether it's still there.
  async function handleVerify(rental) {
    setBusyRnId(rental.rnId);
    try {
      const res = await verifyRentalStatus(rental.rnId);
      if (res.data?.success) {
        toast.success(res.data.message);
        if (res.data.removed) loadRentals();
      } else {
        toast.error(res.data?.message ?? 'Could not verify');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setBusyRnId(null);
  }

  if (loading) return <div className="bf-empty"><span className="spinner spinner-dark" /> Loading…</div>;

  return (
    <div className="bf-page" style={{ maxWidth: 900 }}>
      <PageHeader
        title="Rent Space"
        subtitle="Let customers register their own WhatsApp number under this platform's own WABA, billed monthly via PocketFi — no Meta developer account needed on their end."
      />

      <div className="bf-card" style={{ borderColor: configured ? 'var(--bf-success)' : 'var(--bf-warning)' }}>
        <div className="bf-card-body" style={{ fontSize: 13, color: configured ? 'var(--bf-success)' : 'var(--bf-warning)', fontWeight: 600 }}>
          {configured
            ? `✅ Rent Space is live — ${numbersInUse} of 20 numbers in use on this WABA (Meta's cap applies across your whole Business Manager, not per WABA — see note below).`
            : '⚠️ Not configured yet — set a token, WABA ID, App Secret, and monthly price below to enable this for customers.'}
        </div>
      </div>

      <form onSubmit={handleSave} className="bf-card">
        <div className="bf-card-hdr">Platform WhatsApp Credentials</div>
        <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="bf-field">
            <label className="bf-field-label">WhatsApp Access Token</label>
            <input className="bf-input" value={form.token} onChange={e => setForm({ ...form, token: e.target.value })} placeholder="EAAxxxx…" />
            <div className="bf-field-hint">A System User token with whatsapp_business_management + business_management permissions. Leave unchanged (showing as ••••) to keep the current value.</div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label">WABA ID</label>
            <input className="bf-input" value={form.wabaId} onChange={e => setForm({ ...form, wabaId: e.target.value })} placeholder="Platform's WhatsApp Business Account ID" />
          </div>
          <div className="bf-field">
            <label className="bf-field-label">
              Meta App ID
              <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6, color: 'var(--bf-text-muted, #9ca3af)' }}>(optional)</span>
            </label>
            <input className="bf-input" value={form.metaAppId} onChange={e => setForm({ ...form, metaAppId: e.target.value })} placeholder="1234567890123456" />
            <div className="bf-field-hint">
              Distinct from the WABA ID above — this is the Meta *App* ID (App Dashboard → App Settings → Basic → App ID).
              Only needed for the profile photo upload feature (Resumable Upload API). Same idea as the rest of this
              form: set it once here and it's used automatically for every rented number, so customers never need
              their own Meta App.
            </div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label">App Secret</label>
            <input className="bf-input" value={form.appSecret} onChange={e => setForm({ ...form, appSecret: e.target.value })} placeholder="From Meta App Dashboard → App Settings → Basic" />
            <div className="bf-field-hint">
              The Meta App Secret for the app tied to the token above (App Dashboard → App Settings → Basic → App Secret, click "Show"). Meta signs every webhook with this — without it, incoming messages on rented numbers get silently dropped as "Signature mismatch." One value covers every rented number; renters never see or need this. Leave unchanged (showing as ••••) to keep the current value.
            </div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label">Monthly Price (₦)</label>
            <input className="bf-input" type="number" min="0" step="1" value={form.monthlyPrice}
              onChange={e => setForm({ ...form, monthlyPrice: e.target.value })} placeholder="e.g. 5000" />
            <div className="bf-field-hint">Charged via PocketFi checkout — reuses the PocketFi credentials already configured on the PocketFi settings page.</div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label">
              Max Spaces Per User
              <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6, color: 'var(--bf-text-muted, #9ca3af)' }}>(optional)</span>
            </label>
            <input className="bf-input" type="number" min="0" step="1" value={form.maxPerUser}
              onChange={e => setForm({ ...form, maxPerUser: e.target.value })} placeholder="Leave blank for unlimited" />
            <div className="bf-field-hint">
              How many rented numbers one customer may hold at once. Each rented number can have exactly one bot
              created from it for free — extra bots on the platform's shared WABA still cost the platform Meta
              phone-number slots, so this caps how many any single customer can occupy. Leave blank or 0 for no limit.
            </div>
          </div>
          <div className="bf-field">
            <label className="bf-field-label">
              General Flow Endpoint URL
              <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6, color: 'var(--bf-text-muted, #9ca3af)' }}>(optional)</span>
            </label>
            <input className="bf-input" value={form.flowEndpointUrl}
              onChange={e => setForm({ ...form, flowEndpointUrl: e.target.value })}
              placeholder="Leave blank to use this platform's own endpoint automatically" />
            <div className="bf-field-hint">
              Used as the Flow data-exchange / health-check endpoint for every rented number's Flows, so customers
              don't need their own server. Leave blank and each rented number automatically uses this platform's own
              built-in endpoint instead (api/bots/exchange) — only set this if you want rented-number Flow traffic
              routed somewhere else.
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving} style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>

      <SyncPlatformIdsPanel />

      <LeakedFlowsPanel />

      <div className="bf-card">
        <div className="bf-card-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Rented Numbers ({rentals.length})</span>
          <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 12px' }} onClick={loadRentals} disabled={rentalsLoading}>
            ↻ Refresh
          </button>
        </div>
        <div className="bf-card-body">
          {rentalsLoading ? (
            <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div>
          ) : rentals.length === 0 ? (
            <div style={{ fontSize: 13, color: '#9ca3af' }}>No one has rented a number yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rentals.map(r => {
                const meta = STATUS_META[r.rnStatus] ?? STATUS_META.pending_payment;
                return (
                  <div key={r.rnId} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                    border: '1px solid #e5e7eb', borderRadius: 10, flexWrap: 'wrap',
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 10, background: meta.bg, color: meta.color }}>
                      {meta.label}
                    </span>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                        {r.rnCountryCode && r.rnPhoneNumber ? `+${r.rnCountryCode}${r.rnPhoneNumber}` : '(not yet registered)'}
                        {r.rnDisplayName ? ` — ${r.rnDisplayName}` : ''}
                      </div>
                      <div style={{ fontSize: 11.5, color: '#9ca3af' }}>
                        {r.sEmail} {r.sFname ? `(${r.sFname} ${r.sLname || ''})` : ''} · ₦{Number(r.rnMonthlyAmount).toLocaleString()}/mo
                        {r.rnNextBillingAt ? ` · next billing ${new Date(r.rnNextBillingAt).toLocaleDateString()}` : ''}
                        {r.rnStatus === 'active' && r.rnNextBillingAt && new Date(r.rnNextBillingAt) <= new Date() && (
                          <span style={{ color: '#c0392b', fontWeight: 700 }}> · ⚠ overdue, bot paused</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {(r.rnStatus === 'active' || r.rnStatus === 'paused') && (
                        <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 10px' }}
                          onClick={() => handlePauseResume(r)} disabled={busyRnId === r.rnId}>
                          {r.rnStatus === 'active' ? 'Pause' : 'Resume'}
                        </button>
                      )}
                      {r.rnLinkedBotId && (
                        <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 10px' }}
                          onClick={() => setReviewBId(r.rnLinkedBotId)}
                          title="See which local flows are on this customer's bot and remove any that leaked in from other renters">
                          🌊 Review Flows
                        </button>
                      )}
                      {r.rnPhoneNumberId && (
                        <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 10px' }}
                          onClick={() => handleVerify(r)} disabled={busyRnId === r.rnId}
                          title="Check whether this number still exists on Meta's side">
                          {busyRnId === r.rnId ? '…' : '✓ Verify'}
                        </button>
                      )}
                      {r.rnPhoneNumberId && r.rnStatus !== 'deregistered' && (
                        <button className="btn btn-danger" style={{ fontSize: 12, padding: '5px 10px' }}
                          onClick={() => setDeregisterTarget(r)}>
                          Deregister
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {deregisterTarget && (
        <DeregisterConfirmModal
          rental={deregisterTarget}
          onClose={() => setDeregisterTarget(null)}
          onDeregistered={loadRentals}
        />
      )}

      {reviewBId && (
        <BotFlowsReviewModal bId={reviewBId} onClose={() => setReviewBId(null)} />
      )}
    </div>
  );
}